import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  fetchUserReflections,
  fetchReflectionFilterOptions,
  consolidateReflections
} from '../../services/dashboardService';
import {
  MessageSquare,
  ArrowLeft,
  Calendar,
  Users,
  ChevronRight,
  Loader2,
  Search,
  ChevronLeft,
  Filter,
  X,
  Check,
  GitMerge,
  CheckSquare,
  Square,
  Link2,
  Mail,
  Globe
} from 'lucide-react';

const THEME_LABELS = {
  feedback: 'Feedback',
  team_conflict: 'Team Conflict',
  delegation: 'Delegation',
  decision_making: 'Decision Making',
  one_to_one: 'One-to-One',
  difficult_conversation: 'Difficult Conversation',
  project_update: 'Project Update',
  general: 'General'
};

const TIME_BUCKETS = [
  { key: 'week', label: 'This week', days: 7 },
  { key: 'month', label: 'This month', days: 30 },
  { key: 'quarter', label: 'Last 3 months', days: 90 }
];

const CHANNEL_META = {
  email: { label: 'Email', Icon: Mail },
  web: { label: 'Web', Icon: Globe },
  merged: { label: 'Merged', Icon: GitMerge }
};

const LIMIT = 20;

const ReflectionsPage = () => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Filter state (read from URL for shareability)
  const personFilter = useMemo(() => (searchParams.get('person') || '').split(',').filter(Boolean), [searchParams]);
  const themeFilter = useMemo(() => (searchParams.get('theme') || '').split(',').filter(Boolean), [searchParams]);
  const channelFilter = useMemo(() => (searchParams.get('channel') || '').split(',').filter(Boolean), [searchParams]);
  const timeFilter = searchParams.get('time') || '';

  const [reflections, setReflections] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [offset, setOffset] = useState(0);

  // Filter options
  const [filterOptions, setFilterOptions] = useState({ people: [], themes: [], channels: [] });
  const [openFilter, setOpenFilter] = useState(null); // 'person' | 'theme' | 'channel' | 'time' | null

  // Selection mode
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showConsolidateModal, setShowConsolidateModal] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Reset pagination whenever filters change
  useEffect(() => {
    setOffset(0);
  }, [searchParams]);

  // Build 'since' iso string from time bucket
  const sinceIso = useMemo(() => {
    const bucket = TIME_BUCKETS.find(b => b.key === timeFilter);
    if (!bucket) return null;
    const d = new Date();
    d.setDate(d.getDate() - bucket.days);
    return d.toISOString();
  }, [timeFilter]);

  const loadReflections = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchUserReflections({
        limit: LIMIT,
        offset,
        person: personFilter,
        theme: themeFilter,
        channel: channelFilter,
        since: sinceIso
      });
      setReflections(data.reflections || []);
      setTotal(data.total || 0);
    } catch (err) {
      if (err.message === 'Unauthorized') {
        navigate('/login', { replace: true });
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [offset, personFilter, themeFilter, channelFilter, sinceIso, navigate]);

  useEffect(() => {
    if (isAuthenticated) loadReflections();
  }, [isAuthenticated, loadReflections]);

  // Load filter options once authed
  useEffect(() => {
    const loadOptions = async () => {
      try {
        const data = await fetchReflectionFilterOptions();
        setFilterOptions({
          people: data.people || [],
          themes: data.themes || [],
          channels: data.channels || []
        });
      } catch {
        /* non-fatal */
      }
    };
    if (isAuthenticated) loadOptions();
  }, [isAuthenticated]);

  // Filter search results client-side (on top of server-filtered list)
  const filteredReflections = reflections.filter(reflection => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      (reflection.preview || '').toLowerCase().includes(query) ||
      (reflection.subject || '').toLowerCase().includes(query) ||
      (reflection.people_involved || []).some(p => p.toLowerCase().includes(query))
    );
  });

  const totalPages = Math.ceil(total / LIMIT);
  const currentPage = Math.floor(offset / LIMIT) + 1;

  // Filter updaters
  const updateFilter = (key, values) => {
    const next = new URLSearchParams(searchParams);
    if (!values || values.length === 0) {
      next.delete(key);
    } else if (Array.isArray(values)) {
      next.set(key, values.join(','));
    } else {
      next.set(key, values);
    }
    setSearchParams(next, { replace: true });
  };

  const toggleMulti = (key, current, value) => {
    const next = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
    updateFilter(key, next);
  };

  const clearAllFilters = () => {
    setSearchParams({}, { replace: true });
  };

  const hasActiveFilters = personFilter.length || themeFilter.length || channelFilter.length || timeFilter;

  // Selection
  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedIds([]);
  };

  const handleConsolidate = async (title) => {
    try {
      const result = await consolidateReflections(selectedIds, title);
      exitSelectMode();
      setShowConsolidateModal(false);
      // Reload list & navigate to merged thread
      await loadReflections();
      if (result.merged_conversation_id) {
        navigate(`/dashboard/reflections/${result.merged_conversation_id}`);
      }
    } catch (err) {
      setError(err.message || 'Failed to consolidate');
      setShowConsolidateModal(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-red-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 pb-24">
      {/* Header */}
      <header className="border-b border-zinc-800 sticky top-0 bg-zinc-950/95 backdrop-blur z-20">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4 min-w-0">
              <Link to="/dashboard" className="text-zinc-400 hover:text-white transition-colors" data-testid="reflections-back">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div className="min-w-0">
                <h1 className="text-xl font-bold text-white truncate">Your Reflections</h1>
                <p className="text-zinc-500 text-sm">
                  {total} total{hasActiveFilters ? ' (filtered)' : ''}
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                if (selectMode) exitSelectMode();
                else setSelectMode(true);
              }}
              className={`text-sm px-3 py-1.5 rounded-lg border transition-colors ${
                selectMode
                  ? 'bg-red-500/10 border-red-500/30 text-red-300'
                  : 'bg-zinc-900 border-zinc-800 text-zinc-300 hover:border-zinc-700'
              }`}
              data-testid="reflections-select-toggle"
            >
              {selectMode ? 'Cancel' : 'Select'}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
          <input
            type="text"
            placeholder="Search reflections..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-zinc-900 border border-zinc-800 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:border-zinc-700"
            data-testid="reflections-search-input"
          />
        </div>

        {/* Filter bar */}
        <div className="mb-6" data-testid="reflections-filter-bar">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-zinc-500 text-sm flex items-center gap-1 mr-1">
              <Filter className="w-4 h-4" /> Filter:
            </span>

            <FilterDropdown
              id="person"
              label="Person"
              open={openFilter === 'person'}
              onOpen={() => setOpenFilter(openFilter === 'person' ? null : 'person')}
              onClose={() => setOpenFilter(null)}
              selected={personFilter}
              options={filterOptions.people.map(p => ({ value: p.alias, label: p.alias, count: p.count }))}
              onToggle={(v) => toggleMulti('person', personFilter, v)}
              onClear={() => updateFilter('person', [])}
            />

            <FilterDropdown
              id="theme"
              label="Theme"
              open={openFilter === 'theme'}
              onOpen={() => setOpenFilter(openFilter === 'theme' ? null : 'theme')}
              onClose={() => setOpenFilter(null)}
              selected={themeFilter}
              options={filterOptions.themes.map(t => ({
                value: t.category,
                label: THEME_LABELS[t.category] || t.category,
                count: t.count
              }))}
              onToggle={(v) => toggleMulti('theme', themeFilter, v)}
              onClear={() => updateFilter('theme', [])}
            />

            <FilterDropdown
              id="channel"
              label="Channel"
              open={openFilter === 'channel'}
              onOpen={() => setOpenFilter(openFilter === 'channel' ? null : 'channel')}
              onClose={() => setOpenFilter(null)}
              selected={channelFilter}
              options={(filterOptions.channels.length
                ? filterOptions.channels
                : [{ channel: 'web', count: 0 }, { channel: 'email', count: 0 }]
              ).map(c => ({
                value: c.channel,
                label: CHANNEL_META[c.channel]?.label || c.channel,
                count: c.count
              }))}
              onToggle={(v) => toggleMulti('channel', channelFilter, v)}
              onClear={() => updateFilter('channel', [])}
            />

            <FilterDropdown
              id="time"
              label="Time"
              single
              open={openFilter === 'time'}
              onOpen={() => setOpenFilter(openFilter === 'time' ? null : 'time')}
              onClose={() => setOpenFilter(null)}
              selected={timeFilter ? [timeFilter] : []}
              options={TIME_BUCKETS.map(b => ({ value: b.key, label: b.label }))}
              onToggle={(v) => updateFilter('time', timeFilter === v ? '' : v)}
              onClear={() => updateFilter('time', '')}
            />

            {hasActiveFilters ? (
              <button
                onClick={clearAllFilters}
                className="text-sm text-zinc-400 hover:text-white underline-offset-2 hover:underline px-2 py-1"
                data-testid="reflections-clear-filters"
              >
                Clear all
              </button>
            ) : null}
          </div>

          {/* Active filter chips */}
          {hasActiveFilters ? (
            <div className="mt-3 flex flex-wrap gap-2">
              {personFilter.map(p => (
                <FilterChip key={`p-${p}`} label={p} onRemove={() => toggleMulti('person', personFilter, p)} />
              ))}
              {themeFilter.map(t => (
                <FilterChip key={`t-${t}`} label={THEME_LABELS[t] || t} onRemove={() => toggleMulti('theme', themeFilter, t)} />
              ))}
              {channelFilter.map(c => (
                <FilterChip key={`c-${c}`} label={CHANNEL_META[c]?.label || c} onRemove={() => toggleMulti('channel', channelFilter, c)} />
              ))}
              {timeFilter ? (
                <FilterChip
                  label={TIME_BUCKETS.find(b => b.key === timeFilter)?.label || timeFilter}
                  onRemove={() => updateFilter('time', '')}
                />
              ) : null}
            </div>
          ) : null}
        </div>

        {/* New chat button (hidden in select mode to reduce clutter) */}
        {!selectMode && (
          <Link
            to="/dashboard/chat"
            className="mb-8 flex items-center justify-center gap-2 w-full py-4 bg-gradient-to-r from-purple-600 to-red-600 hover:from-purple-700 hover:to-red-700 text-white font-medium rounded-xl transition-all"
            data-testid="new-chat-button"
          >
            <MessageSquare className="w-5 h-5" />
            Start New Reflection
          </Link>
        )}

        {error ? (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-red-400 mb-4" data-testid="reflections-error">
            {error}
          </div>
        ) : null}

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-8 h-8 text-red-500 animate-spin" />
          </div>
        ) : filteredReflections.length === 0 ? (
          <div className="text-center py-16" data-testid="reflections-empty">
            <MessageSquare className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">
              {reflections.length === 0 && !hasActiveFilters ? 'No reflections yet' : 'No matches found'}
            </h2>
            <p className="text-zinc-400 max-w-md mx-auto">
              {reflections.length === 0 && !hasActiveFilters
                ? 'Start a conversation with the AI companion to begin your reflective practice.'
                : 'Try adjusting your search or filters.'}
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {filteredReflections.map((reflection) => (
                <ReflectionCard
                  key={reflection.conversation_id}
                  reflection={reflection}
                  selectMode={selectMode}
                  selected={selectedIds.includes(reflection.conversation_id)}
                  onToggleSelect={() => toggleSelect(reflection.conversation_id)}
                  onLongPressStart={() => {
                    if (!selectMode) {
                      setSelectMode(true);
                      setSelectedIds([reflection.conversation_id]);
                    }
                  }}
                />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-8 flex items-center justify-center gap-4">
                <button
                  onClick={() => setOffset(Math.max(0, offset - LIMIT))}
                  disabled={offset === 0}
                  className="flex items-center gap-1 px-4 py-2 bg-zinc-800 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-zinc-700 transition-colors"
                  data-testid="reflections-prev-page"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </button>
                <span className="text-zinc-400">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setOffset(offset + LIMIT)}
                  disabled={offset + LIMIT >= total}
                  className="flex items-center gap-1 px-4 py-2 bg-zinc-800 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-zinc-700 transition-colors"
                  data-testid="reflections-next-page"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </>
        )}
      </main>

      {/* Floating selection bar */}
      {selectMode && selectedIds.length > 0 ? (
        <div
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-30 bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl px-5 py-3 flex items-center gap-4"
          data-testid="reflections-selection-bar"
        >
          <span className="text-white text-sm font-medium">
            {selectedIds.length} selected
          </span>
          <button
            onClick={() => setShowConsolidateModal(true)}
            disabled={selectedIds.length < 2}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-red-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="reflections-consolidate-btn"
          >
            <GitMerge className="w-4 h-4" />
            Consolidate
          </button>
          <button
            onClick={exitSelectMode}
            className="text-zinc-400 hover:text-white"
            data-testid="reflections-selection-clear"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      ) : null}

      {/* Consolidate modal */}
      {showConsolidateModal ? (
        <ConsolidateModal
          reflections={reflections.filter(r => selectedIds.includes(r.conversation_id))}
          onCancel={() => setShowConsolidateModal(false)}
          onConfirm={handleConsolidate}
        />
      ) : null}
    </div>
  );
};

// ----------------- Subcomponents -----------------

const FilterDropdown = ({ id, label, open, onOpen, onClose, selected, options, onToggle, onClear, single = false }) => {
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (open && ref.current && !ref.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, onClose]);

  const count = selected?.length || 0;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={onOpen}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm transition-colors ${
          count > 0
            ? 'bg-purple-500/10 border-purple-500/30 text-purple-200'
            : 'bg-zinc-900 border-zinc-800 text-zinc-300 hover:border-zinc-700'
        }`}
        data-testid={`reflections-filter-${id}`}
      >
        {label}
        {count > 0 ? (
          <span className="bg-purple-500/30 text-purple-100 text-xs rounded-full px-1.5 py-0.5 ml-1">
            {count}
          </span>
        ) : null}
        <ChevronRight className={`w-3 h-3 transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>

      {open ? (
        <div
          className="absolute left-0 mt-2 min-w-[220px] max-h-80 overflow-auto bg-zinc-900 border border-zinc-800 rounded-lg shadow-xl z-30 py-1"
          data-testid={`reflections-filter-${id}-dropdown`}
        >
          {options.length === 0 ? (
            <div className="px-3 py-3 text-sm text-zinc-500">No options available</div>
          ) : (
            options.map((opt) => {
              const isSelected = selected?.includes(opt.value);
              return (
                <button
                  key={opt.value}
                  onClick={() => {
                    onToggle(opt.value);
                    if (single) onClose();
                  }}
                  className="w-full flex items-center justify-between gap-3 px-3 py-2 text-sm text-zinc-200 hover:bg-zinc-800 transition-colors"
                  data-testid={`reflections-filter-${id}-option-${opt.value}`}
                >
                  <span className="flex items-center gap-2">
                    {isSelected ? (
                      <Check className="w-4 h-4 text-purple-400" />
                    ) : (
                      <span className="w-4 h-4" />
                    )}
                    <span>{opt.label}</span>
                  </span>
                  {typeof opt.count === 'number' && opt.count > 0 ? (
                    <span className="text-xs text-zinc-500">{opt.count}</span>
                  ) : null}
                </button>
              );
            })
          )}
          {selected && selected.length > 0 ? (
            <div className="border-t border-zinc-800 mt-1 pt-1">
              <button
                onClick={() => {
                  onClear();
                  onClose();
                }}
                className="w-full text-left px-3 py-2 text-sm text-zinc-400 hover:text-white hover:bg-zinc-800"
                data-testid={`reflections-filter-${id}-clear`}
              >
                Clear selection
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

const FilterChip = ({ label, onRemove }) => (
  <span className="inline-flex items-center gap-1.5 bg-purple-500/10 border border-purple-500/30 text-purple-200 text-xs rounded-full px-2.5 py-1">
    {label}
    <button
      onClick={onRemove}
      className="text-purple-300 hover:text-white"
      aria-label={`Remove filter ${label}`}
    >
      <X className="w-3 h-3" />
    </button>
  </span>
);

const LONG_PRESS_MS = 500;

const ReflectionCard = ({ reflection, selectMode, selected, onToggleSelect, onLongPressStart }) => {
  const timerRef = useRef(null);
  const triggeredRef = useRef(false);

  const startLongPress = () => {
    triggeredRef.current = false;
    timerRef.current = setTimeout(() => {
      triggeredRef.current = true;
      onLongPressStart?.();
    }, LONG_PRESS_MS);
  };

  const cancelLongPress = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const handleClick = (e) => {
    if (selectMode) {
      e.preventDefault();
      onToggleSelect();
      return;
    }
    if (triggeredRef.current) {
      e.preventDefault();
    }
  };

  const ChannelIcon = CHANNEL_META[reflection.channel]?.Icon;

  return (
    <Link
      to={`/dashboard/reflections/${reflection.conversation_id}`}
      onClick={handleClick}
      onTouchStart={startLongPress}
      onTouchEnd={cancelLongPress}
      onTouchCancel={cancelLongPress}
      onTouchMove={cancelLongPress}
      className={`block bg-zinc-900 border rounded-xl p-4 transition-colors group ${
        selected ? 'border-purple-500/60 ring-1 ring-purple-500/30' : 'border-zinc-800 hover:border-zinc-700'
      }`}
      data-testid={`reflection-card-${reflection.conversation_id}`}
    >
      <div className="flex items-start gap-4">
        {selectMode ? (
          <div className="flex-shrink-0 pt-1" data-testid={`reflection-select-${reflection.conversation_id}`}>
            {selected ? (
              <CheckSquare className="w-6 h-6 text-purple-400" />
            ) : (
              <Square className="w-6 h-6 text-zinc-500" />
            )}
          </div>
        ) : (
          <div className="w-10 h-10 bg-zinc-800 rounded-lg flex items-center justify-center flex-shrink-0">
            {reflection.is_merged ? (
              <Link2 className="w-5 h-5 text-purple-400" />
            ) : (
              <MessageSquare className="w-5 h-5 text-zinc-400" />
            )}
          </div>
        )}

        <div className="flex-1 min-w-0">
          {reflection.is_merged && reflection.subject ? (
            <p className="text-white font-semibold truncate flex items-center gap-2">
              <Link2 className="w-4 h-4 text-purple-400 flex-shrink-0" />
              {reflection.subject}
            </p>
          ) : null}
          <p className={`text-white line-clamp-2 ${reflection.is_merged ? 'text-sm text-zinc-300 mt-1' : ''}`}>
            {reflection.preview || 'Reflection'}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
            <span className="flex items-center gap-1 text-zinc-500">
              <Calendar className="w-4 h-4" />
              {reflection.created_at ? new Date(reflection.created_at).toLocaleDateString() : ''}
            </span>
            <span className="text-zinc-600">•</span>
            <span className="text-zinc-500">{reflection.message_count} msg</span>

            {ChannelIcon ? (
              <>
                <span className="text-zinc-600">•</span>
                <span className="flex items-center gap-1 text-zinc-500 capitalize">
                  <ChannelIcon className="w-3.5 h-3.5" />
                  {CHANNEL_META[reflection.channel]?.label || reflection.channel}
                </span>
              </>
            ) : null}

            {reflection.situation_category ? (
              <>
                <span className="text-zinc-600">•</span>
                <span className="text-purple-300/80 text-xs bg-purple-500/10 border border-purple-500/20 rounded-full px-2 py-0.5">
                  {THEME_LABELS[reflection.situation_category] || reflection.situation_category}
                </span>
              </>
            ) : null}

            {reflection.people_involved?.length > 0 && (
              <>
                <span className="text-zinc-600">•</span>
                <span className="flex items-center gap-1 text-zinc-500">
                  <Users className="w-4 h-4" />
                  {reflection.people_involved.join(', ')}
                </span>
              </>
            )}

            {reflection.is_merged && reflection.merged_from_count ? (
              <>
                <span className="text-zinc-600">•</span>
                <span className="text-purple-300 text-xs">
                  Merged from {reflection.merged_from_count} threads
                </span>
              </>
            ) : null}
          </div>
        </div>
        {!selectMode ? (
          <ChevronRight className="w-5 h-5 text-zinc-600 group-hover:text-zinc-400 transition-colors flex-shrink-0" />
        ) : null}
      </div>
    </Link>
  );
};

const ConsolidateModal = ({ reflections, onCancel, onConfirm }) => {
  const allPeople = [...new Set(reflections.flatMap(r => r.people_involved || []))];
  const channels = [...new Set(reflections.map(r => r.channel).filter(Boolean))];
  const dates = reflections.map(r => r.created_at).filter(Boolean).sort();
  const dateRange = dates.length
    ? `${new Date(dates[0]).toLocaleDateString()} – ${new Date(dates[dates.length - 1]).toLocaleDateString()}`
    : '';

  const suggestedTitle = allPeople.length
    ? `${allPeople.slice(0, 2).join(', ')} - Consolidated`
    : `Consolidated thread (${reflections.length} reflections)`;
  const [title, setTitle] = useState(suggestedTitle);
  const [saving, setSaving] = useState(false);

  const handleConfirm = async () => {
    setSaving(true);
    await onConfirm(title.trim() || suggestedTitle);
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40 flex items-center justify-center p-4"
      data-testid="reflections-consolidate-modal"
    >
      <div className="bg-zinc-900 border border-zinc-700 rounded-2xl max-w-lg w-full p-6">
        <h3 className="text-lg font-semibold text-white mb-1">
          Consolidate {reflections.length} Reflections
        </h3>
        <p className="text-zinc-400 text-sm mb-5">
          Messages will be merged in chronological order. Original threads will be hidden from your main list but can be restored later.
        </p>

        <label className="block text-sm text-zinc-300 mb-2" htmlFor="consolidate-title">
          Combined thread title
        </label>
        <input
          id="consolidate-title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded-lg text-white focus:outline-none focus:border-purple-500 mb-4"
          data-testid="consolidate-title-input"
        />

        <div className="space-y-2 bg-zinc-950 border border-zinc-800 rounded-lg p-3 mb-5 text-sm">
          {allPeople.length > 0 ? (
            <div className="flex gap-2">
              <span className="text-zinc-500 w-28 flex-shrink-0">People included:</span>
              <span className="text-zinc-200">{allPeople.join(', ')}</span>
            </div>
          ) : null}
          {channels.length > 0 ? (
            <div className="flex gap-2">
              <span className="text-zinc-500 w-28 flex-shrink-0">Channels:</span>
              <span className="text-zinc-200">{channels.join(', ')}</span>
            </div>
          ) : null}
          {dateRange ? (
            <div className="flex gap-2">
              <span className="text-zinc-500 w-28 flex-shrink-0">Date range:</span>
              <span className="text-zinc-200">{dateRange}</span>
            </div>
          ) : null}
        </div>

        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            disabled={saving}
            className="px-4 py-2 bg-zinc-800 text-zinc-200 rounded-lg hover:bg-zinc-700 disabled:opacity-50"
            data-testid="consolidate-cancel-btn"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={saving || !title.trim()}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-red-600 text-white rounded-lg disabled:opacity-50"
            data-testid="consolidate-confirm-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <GitMerge className="w-4 h-4" />}
            Consolidate
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReflectionsPage;
